// Layer 4 (Data Schema, module-local): Documents (Modules 7 + 8) — paul-tarp.A20 (#422), `a20-documents` stream.
// [syn] screenshot-grade seed — procedures expanded from the functional description, a round sheet with this
// shift's completion, and training material revised from a lesson learned (v19 M7 671-722, M8 723-756).
// Interlock ids/labels/override authorities match the `a20-risk` stream's IL-01..09 (v19 M6 §4 order, common brief).
// The gangue-association-drift case (case-gangue / co-gangue / FMR-0008 / dr-mineralogy) is real seed already
// carried by Advisory (`bunker-hill-advisory-seed.ts`) — Training material's revision traces to it, not invented.

import type { Persona } from '@/shared/types';
import type { PlantRoleId } from '@/versions/v1/schema/plant-role';
import type { CrewId, SeatId } from '@/versions/v1/schema/seat';

// ---------------------------------------------------------------------------
// Shared vocabulary
// ---------------------------------------------------------------------------

// Matches `a20-risk`'s `OverrideAuthority` union exactly (`risk/seed.ts:11`); display-only
// remapping to the brief's "No override" wording happens in `Scene.transform.ts`, not here.
export type OverrideAuthority = 'No one' | 'Supervisor only' | 'Maintenance mode' | 'Operator';

export type InterlockRef = {
  id: string; // 'IL-01' .. 'IL-09' — must match a20-risk
  label: string;
  overrideAuthority: OverrideAuthority;
};

/**
 * The nine safety interlocks, v19 M6 §4 order — copied verbatim per the common brief.
 * Conditions and override authorities are read off `a20-risk/src/versions/v1/modules/risk/seed.ts`
 * (the interlock source of truth) so the two screens agree (MF1, SF2).
 */
export const INTERLOCKS: readonly InterlockRef[] = [
  { id: 'IL-01', label: 'BM01 HP lube pressure LL (PT1103)', overrideAuthority: 'No one' },
  { id: 'IL-02', label: 'BM01 trunnion bearing temp HH (TT1104)', overrideAuthority: 'No one' },
  { id: 'IL-03', label: 'BM01 motor power HH > 1,200 kW', overrideAuthority: 'No one' },
  { id: 'IL-04', label: 'BM01 barring drive engaged', overrideAuthority: 'No one' },
  { id: 'IL-05', label: 'GP40 discharge pressure HH > 5,000 kPa', overrideAuthority: 'No one' },
  { id: 'IL-06', label: 'Fine ore bin LL (<15%)', overrideAuthority: 'Supervisor only' },
  { id: 'IL-07', label: 'FOB bin level HH (80%)', overrideAuthority: 'Supervisor only' },
  { id: 'IL-08', label: 'TK01/TK02 rake torque HH (85%)', overrideAuthority: 'Maintenance mode' },
  { id: 'IL-09', label: 'GP40 suction pressure LL (<50 kPa)', overrideAuthority: 'Operator' },
];

export function interlockById(id: string): InterlockRef | undefined {
  return INTERLOCKS.find((row) => row.id === id);
}

export type DocumentGroupId = 'procedures' | 'round-sheets' | 'training';
export type ProcedureType = 'Start-up' | 'Shutdown' | 'Routine' | 'Emergency';

// ---------------------------------------------------------------------------
// Procedures (Module 7 §2-3)
// ---------------------------------------------------------------------------

export type ProcedureStep = {
  sequenceNo: number;
  instruction: string;
  safetyNote?: string;
  verification?: string;
  interlockIds?: string[];
};

export type ControlLoopExpectation = {
  tag: string;
  expectedMode: string;
};

export type ProcedureChangeEvent = {
  id: string;
  revision: string;
  at: string;
  summary: string;
  flagsTrainingId?: string;
};

export type ProcedureDoc = {
  kind: 'procedure';
  id: string;
  title: string;
  requiredRoleIds: readonly PlantRoleId[];
  criticalSop: boolean;
  procedureType: ProcedureType;
  version: string;
  updatedAt: string;
  sourceSections: string[];
  controlLoops: ControlLoopExpectation[];
  steps: ProcedureStep[];
  changeEvents: ProcedureChangeEvent[];
};

export const PROCEDURES: readonly ProcedureDoc[] = [
  {
    kind: 'procedure',
    id: 'PR-01',
    title: 'BM01 ball mill start-up',
    requiredRoleIds: ['cro', 'ops-supervisor', 'superintendent', 'metallurgist', 'senior-metallurgist', 'maintenance'],
    criticalSop: true,
    procedureType: 'Start-up',
    version: 'r3',
    updatedAt: '18 May 2026',
    sourceSections: ['Trunnion lube', 'Motor', 'Barring drive'],
    controlLoops: [
      { tag: 'BM01', expectedMode: 'REMOTE / AUTO' },
      { tag: 'WIC1201A', expectedMode: 'AUTO (ramping to Budget/Stretch feed)' },
    ],
    steps: [
      {
        sequenceNo: 1,
        instruction:
          'Confirm BM01 barring drive is disengaged (BM01BG = OFF) and the REMOTE/LOCAL switch at the LCU is set to REMOTE.',
        safetyNote: 'Mill start is blocked while the barring drive is engaged.',
        interlockIds: ['IL-04'],
      },
      {
        sequenceNo: 2,
        instruction:
          'Confirm the HP trunnion lube system (BM01TLSYS) is running and healthy: oil level not Low Low, oil temperature below 65 °C, no trip flag active.',
        safetyNote: 'Trunnion bearing oil pressure LL trips the motor immediately once running. Verify before starting, not after.',
        interlockIds: ['IL-01', 'IL-02'],
      },
      {
        sequenceNo: 3,
        instruction: 'Confirm the pinion bearing lube system is running.',
        safetyNote: 'Pinion bearing temp HH is a motor trip condition.',
      },
      {
        sequenceNo: 4,
        instruction: 'Confirm the ring gear lube system is in AUTO with no fault.',
      },
      {
        sequenceNo: 5,
        instruction:
          'Confirm cyclone feed pump GP01 (or standby GP01S) is running, or feed conveyor CV02 is stopped.',
        safetyNote: 'Prevents starting the mill into a cyclone circuit that cannot yet receive discharge.',
      },
      {
        sequenceNo: 6,
        instruction: 'Select DCS faceplate BM01 to RUN.',
        safetyNote: 'Motor controller TRIP and LOCK conditions must already be healthy. Resolve any active fault before this step, not after.',
        verification: 'BM01 faceplate reads RUNNING within 10 s of selection.',
      },
      {
        sequenceNo: 7,
        instruction:
          "Ramp mill feed via WIC1201A toward the shift's Budget/Stretch feed rate target.",
        safetyNote: 'Motor power JI1210 trips at >1,200 kW for 5 s. Monitor through the ramp alongside trunnion bearing temperature and pressure.',
        interlockIds: ['IL-03'],
      },
      {
        sequenceNo: 8,
        instruction:
          'Confirm mill discharge reports normally to the Cyclone Pump Box (LT-169/171/014) and cyclone feed pressure PI1310 settles into the 80-140 kPa design band.',
        verification: 'PI1310 holding 80-140 kPa, not cycling, for two consecutive minutes.',
      },
    ],
    changeEvents: [
      {
        id: 'ce-pr01-r3',
        revision: 'r3',
        at: '18 May 2026',
        summary:
          'Step 2 tightened: trunnion lube oil-temperature verification added after a bearing-temperature near-miss.',
        flagsTrainingId: 'TM-03',
      },
    ],
  },
  {
    kind: 'procedure',
    id: 'PR-02',
    title: 'BM01 ball mill shutdown',
    requiredRoleIds: ['cro', 'ops-supervisor', 'superintendent', 'metallurgist', 'senior-metallurgist', 'maintenance'],
    criticalSop: true,
    procedureType: 'Shutdown',
    version: 'r2',
    updatedAt: '02 Apr 2026',
    sourceSections: ['Motor', 'Trunnion lube'],
    controlLoops: [{ tag: 'BM01', expectedMode: 'REMOTE (ramping to zero)' }],
    steps: [
      {
        sequenceNo: 1,
        instruction: 'Ramp mill feed to zero via WIC1201A; hold until cyclone feed pressure PI1310 falls below 40 kPa.',
        verification: 'WIC1201A reads 0 t/h.',
      },
      {
        sequenceNo: 2,
        instruction: 'Select DCS faceplate BM01 to STOP once the mill sump has drawn down.',
        safetyNote: 'Do not stop under full charge: packs the sump and strands solids in the mill.',
      },
      {
        sequenceNo: 3,
        instruction: 'Confirm BM01 has coasted to a full stop before engaging the barring drive.',
        interlockIds: ['IL-04'],
      },
      {
        sequenceNo: 4,
        instruction: 'Leave the HP trunnion lube system running for 20 minutes post-stop to cool the bearings.',
        interlockIds: ['IL-01'],
      },
    ],
    changeEvents: [],
  },
  {
    kind: 'procedure',
    id: 'PR-03',
    title: 'Fine ore bin and conveyor walk-down',
    requiredRoleIds: ['cro', 'ops-supervisor'],
    criticalSop: false,
    procedureType: 'Routine',
    version: 'r1',
    updatedAt: '11 Jan 2026',
    sourceSections: ['Fine ore reclaim', 'CV02 feed conveyor'],
    controlLoops: [{ tag: 'CV02', expectedMode: 'AUTO (run-permissive)' }],
    steps: [
      {
        sequenceNo: 1,
        instruction: 'Visually confirm the fine ore bin level is holding above the reclaim cone and below the overflow chute.',
        interlockIds: ['IL-06', 'IL-07'],
      },
      {
        sequenceNo: 2,
        instruction: 'Check CV02 belt tracking and skirting for material build-up.',
      },
      {
        sequenceNo: 3,
        instruction: 'Confirm reclaim feeder amperage is steady, consistent with bin head.',
      },
    ],
    changeEvents: [],
  },
  {
    kind: 'procedure',
    id: 'PR-04',
    title: 'Paste plant emergency stop',
    requiredRoleIds: ['cro', 'ops-supervisor', 'superintendent', 'maintenance'],
    criticalSop: true,
    procedureType: 'Emergency',
    version: 'r2',
    updatedAt: '27 Feb 2026',
    sourceSections: ['Thickening', 'Paste pump'],
    controlLoops: [{ tag: 'GP40', expectedMode: 'AUTO (suspended on E-stop)' }],
    steps: [
      {
        sequenceNo: 1,
        instruction: 'Press the nearest paste-plant E-stop; confirm GP40 has tripped and locked out.',
        safetyNote: 'GP40 discharge pressure HH and suction pressure LL both alarm on trip. Expected, not a fault to clear before isolating.',
        interlockIds: ['IL-05', 'IL-09'],
      },
      {
        sequenceNo: 2,
        instruction: 'Confirm TK01/TK02 rake drives have stepped to their overload-protection torque limit rather than stalled.',
        safetyNote: 'Rake torque HH is a maintenance-mode-only override. Do not clear it from the control room.',
        interlockIds: ['IL-08'],
      },
      {
        sequenceNo: 3,
        instruction: 'Isolate GP40 at the local disconnect once the line has drained down.',
      },
    ],
    changeEvents: [],
  },
];

// ---------------------------------------------------------------------------
// Round sheets (Module 7 §4)
// ---------------------------------------------------------------------------

export type RoundTaskStatus = 'done' | 'flagged' | 'pending';

export type RoundTask = {
  id: string;
  check: string;
  tag: string;
  goodLooksLike: string;
  badLooksLike: string;
  frequency: string;
  dueAt: string;
  status: RoundTaskStatus;
  reading?: string;
};

export type RoundSheetDoc = {
  kind: 'round-sheet';
  id: string;
  title: string;
  requiredRoleIds: readonly PlantRoleId[];
  criticalSop: boolean;
  circuit: string;
  version: string;
  updatedAt: string;
  shiftLabel: string;
  tasks: readonly RoundTask[];
};

export const ROUND_SHEETS: readonly RoundSheetDoc[] = [
  {
    kind: 'round-sheet',
    id: 'RS-01',
    title: 'Grinding circuit round sheet',
    requiredRoleIds: ['cro', 'ops-supervisor', 'metallurgist', 'senior-metallurgist'],
    criticalSop: false,
    circuit: 'Grinding',
    version: 'v4',
    updatedAt: '01 Jun 2026',
    shiftLabel: 'Day shift · C crew',
    tasks: [
      {
        id: 'rt-01',
        check: 'Cyclone feed density/pressure',
        tag: 'DT-203 / PT-173',
        goodLooksLike: 'Stable within 57.5% solids, 80-140 kPa',
        badLooksLike: 'PT-173 cycling — sign of roping or surging cyclones',
        frequency: 'Hourly',
        dueAt: '06:00',
        status: 'done',
        reading: '57.3% · 118 kPa',
      },
      {
        id: 'rt-02',
        check: 'Cyclone Pump Box levels',
        tag: 'LT-169/171/014',
        goodLooksLike: 'Holding mid-range, no surging',
        badLooksLike: 'Trending to high/low alarm — cavitation or overflow risk',
        frequency: 'Hourly',
        dueAt: '07:00',
        status: 'done',
        reading: 'Mid-range, steady',
      },
      {
        id: 'rt-03',
        check: 'Mill motor power',
        tag: 'JT-023',
        goodLooksLike: 'Steady, consistent with ore competency',
        badLooksLike: 'Dropping at constant feed — possible liner wear or ball charge issue',
        frequency: 'Hourly',
        dueAt: '08:00',
        status: 'done',
        reading: '1,040 kW · steady',
      },
      {
        id: 'rt-04',
        check: 'Cyclone overflow density',
        tag: 'DT-210',
        goodLooksLike: '~37% solids to flotation feed',
        badLooksLike: 'Rising above 37% — grind may be coarsening',
        frequency: 'Hourly',
        dueAt: '09:00',
        status: 'done',
        reading: '37.2% solids',
      },
      {
        id: 'rt-05',
        check: 'Cyclone feed pressure (repeat scan)',
        tag: 'PT-173',
        goodLooksLike: 'Stable within 80-140 kPa, not cycling',
        badLooksLike: 'PT-173 cycling — sign of roping or surging cyclones',
        frequency: 'Hourly',
        dueAt: '10:00',
        status: 'flagged',
        reading: '148-156 kPa, cycling',
      },
      {
        id: 'rt-06',
        check: 'Process water',
        tag: 'FIT-022',
        goodLooksLike: 'Tracking ~300 gpm setpoint',
        badLooksLike: 'Deviating — cyclone feed density will drift, changing grind size',
        frequency: 'Hourly',
        dueAt: '11:00',
        status: 'done',
        reading: '298 gpm',
      },
      {
        id: 'rt-07',
        check: 'Circulating load',
        tag: 'Calculated',
        goodLooksLike: '~186 stph / 248% — stable',
        badLooksLike: 'Rising trend — leading indicator that grind is coarsening',
        frequency: 'Per-shift',
        dueAt: '06:00',
        status: 'done',
        reading: '189 stph / 251%',
      },
      {
        id: 'rt-08',
        check: 'Grinding circuit lab sample',
        tag: 'SA-03',
        goodLooksLike: 'PSA on-target for the shift composite',
        badLooksLike: 'PSA coarsening vs the trailing 7-day mean',
        frequency: 'Per-shift',
        dueAt: '12:00',
        status: 'pending',
      },
    ],
  },
  {
    kind: 'round-sheet',
    id: 'RS-02',
    title: 'Zn flotation circuit round sheet',
    requiredRoleIds: ['cro', 'ops-supervisor', 'metallurgist', 'senior-metallurgist'],
    criticalSop: false,
    circuit: 'Zn flotation',
    version: 'v2',
    updatedAt: '14 Mar 2026',
    shiftLabel: 'Day shift · C crew',
    tasks: [
      {
        id: 'rt-z01',
        check: 'Zn concentrate grade',
        tag: 'Lab · SA-05',
        goodLooksLike: '≥ 58.0% Zn',
        badLooksLike: 'Trending down: check froth stability and gangue entrainment',
        frequency: 'Per-shift',
        dueAt: '06:00',
        status: 'done',
        reading: '58.6% Zn',
      },
      {
        id: 'rt-z02',
        check: 'Rougher froth stability',
        tag: 'Visual',
        goodLooksLike: 'Even bubble size, steady crowding',
        badLooksLike: 'Froth collapsing or over-stable: air/reagent off design',
        frequency: 'Hourly',
        dueAt: '07:00',
        status: 'done',
        reading: 'Even, steady',
      },
      {
        id: 'rt-z03',
        check: 'Zn rougher pH',
        tag: 'AT-512',
        goodLooksLike: 'Holding setpoint ± 0.2',
        badLooksLike: 'Drifting: check lime addition',
        frequency: 'Hourly',
        dueAt: '08:00',
        status: 'done',
        reading: 'On setpoint',
      },
      {
        id: 'rt-z04',
        check: 'Zn thickener underflow density',
        tag: 'DT-611',
        goodLooksLike: 'Within design band, no surging',
        badLooksLike: 'Rake torque climbing: check for a rising mud bed',
        frequency: 'Hourly',
        dueAt: '09:00',
        status: 'pending',
      },
      {
        id: 'rt-z05',
        check: 'SMBS dosing vs design',
        tag: 'FIT-741',
        goodLooksLike: 'Tracking design rate, no batch-age drift',
        badLooksLike: 'Actual above design: carry-over risk to Pb circuit pH',
        frequency: 'Per-shift',
        dueAt: '06:00',
        status: 'done',
        reading: 'On design rate',
      },
      {
        id: 'rt-z06',
        check: 'Zn circuit sample',
        tag: 'SA-05',
        goodLooksLike: 'Assay within 7-day band',
        badLooksLike: 'Grade or recovery outside 7-day band',
        frequency: 'Per-shift',
        dueAt: '10:00',
        status: 'pending',
      },
    ],
  },
];

// ---------------------------------------------------------------------------
// Training material (Module 8)
// ---------------------------------------------------------------------------

export type TrainingSection = {
  heading: string;
  body: string;
};

export type TrainingVersionEntry = {
  version: string;
  publishedAt: string;
  source: string;
};

export type TrainingRevision = {
  id: string;
  status: 'Pending review' | 'Approved' | 'Changes requested';
  sourceCaseId: string;
  sourceCaseLabel: string;
  /** The FMEA row this revision traces to — `fm-gangue.failureMode` (bunker-hill-advisory-seed.ts:1352). */
  fmeaRowLabel: string;
  reviewSeat: string;
  reviewerName: string;
  diffSummary: string;
  insertion: {
    heading: string;
    symptom: string;
    detectionLogic: string;
    immediateResponse: string;
    flaggedNote: string;
  };
};

export type RefresherTarget = {
  personName: string;
  role: string;
  trainedOnVersion: string;
};

export type TrainingDoc = {
  kind: 'training';
  id: string;
  title: string;
  audienceRole: string;
  requiredRoleIds: readonly PlantRoleId[];
  criticalSop: boolean;
  version: string;
  updatedAt: string;
  sourceRefs: string[];
  sections: readonly TrainingSection[];
  versionHistory: readonly TrainingVersionEntry[];
  pendingRevision?: TrainingRevision;
  refresherTargets: readonly RefresherTarget[];
};

export const TRAINING_MODULES: readonly TrainingDoc[] = [
  {
    kind: 'training',
    id: 'TM-01',
    title: 'Control room operator induction: DCS and round sheet basics',
    audienceRole: 'Control room',
    requiredRoleIds: ['cro', 'ops-supervisor', 'superintendent'],
    criticalSop: true,
    version: 'v2',
    updatedAt: '20 Jan 2026',
    sourceRefs: ['Grinding circuit round sheet'],
    sections: [
      {
        heading: 'Reading the round sheet',
        body: 'Each check carries a good-looks-like band and a bad-looks-like symptom. Log the reading, not just a tick, so a trend across shifts is visible.',
      },
      {
        heading: 'Escalation',
        body: 'A flagged reading stays open on the sheet until a supervisor or the metallurgist signs it off. Do not clear it by re-checking alone.',
      },
    ],
    versionHistory: [
      { version: 'v1', publishedAt: '02 Nov 2025', source: 'Initial authoring: round sheet format' },
      { version: 'v2', publishedAt: '20 Jan 2026', source: 'Editorial pass: escalation wording clarified' },
    ],
    refresherTargets: [],
  },
  {
    kind: 'training',
    id: 'TM-02',
    title: 'Maintenance supervisor refresher: BM01 trunnion bearing condition',
    audienceRole: 'Maintenance',
    requiredRoleIds: ['maintenance', 'superintendent'],
    criticalSop: false,
    version: 'v1',
    updatedAt: '05 Jun 2026',
    sourceRefs: ['BM01 ball mill start-up', 'BM01 trunnion bearing condition monitoring'],
    sections: [
      {
        heading: 'Baseline signature',
        body: 'Trunnion bearing temperature trending upward against its own 7-day mean, independent of ambient or load change, is the earliest reliable signal. Condition monitoring carries the trend; the interlock is the last line, not the first.',
      },
      {
        heading: 'Cross-reference',
        body: 'A work order raised against the bearing (WO-441) or a risk register row for the same failure mode (RK-02) both source this trend. Check either before assuming the interlock trip was the first warning.',
      },
    ],
    versionHistory: [{ version: 'v1', publishedAt: '05 Jun 2026', source: 'Initial authoring: condition monitoring trend' }],
    refresherTargets: [],
  },
  {
    kind: 'training',
    id: 'TM-03',
    title: 'Metallurgist onboarding: grade-decline troubleshooting',
    audienceRole: 'Metallurgist',
    requiredRoleIds: ['metallurgist', 'senior-metallurgist'],
    criticalSop: true,
    version: 'v3',
    updatedAt: '01 Jun 2026',
    sourceRefs: ['Pb/Zn recovery diagnostics', 'BM01 ball mill start-up: trunnion lube verification'],
    sections: [
      {
        heading: 'Working the decline',
        body: 'Before treating a grade decline as a process fault, rule out grind: PSA on-target with every reagent, pH and pressure signal on setpoint points away from the flotation circuit.',
      },
      {
        heading: 'Common causes',
        body: 'Silica/gangue entrainment from an unstable froth, SMBS carry-over from batch-age drift, and reagent dosing lagging setpoint account for most Pb and Zn concentrate grade shortfalls.',
      },
    ],
    versionHistory: [
      { version: 'v1', publishedAt: '10 Feb 2026', source: 'Initial authoring: functional description and baseline diagnostics' },
      {
        version: 'v2',
        publishedAt: '20 May 2026',
        source: 'BM01 ball mill start-up revision r3: trunnion lube verification tightened, grind ruled out as a confounder',
      },
      { version: 'v3', publishedAt: '01 Jun 2026', source: 'Editorial pass: Senior metallurgist review' },
    ],
    pendingRevision: {
      id: 'tr-tm03-01',
      status: 'Pending review',
      sourceCaseId: 'case-gangue',
      sourceCaseLabel: 'GANGUE-NEW',
      fmeaRowLabel: 'Gangue mineral association shift (mine block)',
      reviewSeat: 'Senior metallurgist',
      reviewerName: 'L. Chen',
      diffSummary: 'New failure pattern inserted into Common causes: gangue mineral association drift from a specific mine block.',
      insertion: {
        heading: 'Gangue mineral association drift',
        symptom: 'Pb and Zn concentrate grades both declining slowly (about −0.3%/day) with every reagent, pH and PSA signal on setpoint.',
        detectionLogic:
          'Both grades decline slowly AND all reagent/pH/PSA signals stay on setpoint AND the mine has advanced to a new block. Check mineralogy before treating it as a process fault.',
        immediateResponse: 'Cross-check the mine block against the QEMSCAN library before assuming a grind or reagent fault.',
        flaggedNote: 'Recently added · confirmed on site 05 Jun 2026',
      },
    },
    // [syn] Both metallurgists have signed published v3. The pending review is a draft and does
    // not make either current sign-off stale until a new version is published.
    refresherTargets: [
      { personName: 'L. Chen', role: 'Senior metallurgist', trainedOnVersion: 'v3' },
      { personName: 'S. Park', role: 'Metallurgist', trainedOnVersion: 'v3' },
    ],
  },
];

// ---------------------------------------------------------------------------
// Training compliance roster (Module 8)
// ---------------------------------------------------------------------------

export const TRAINING_AS_OF = '2026-06-08';

export type TrainingRosterMember = {
  personId: string;
  seatId: SeatId;
  roleId: PlantRoleId;
  roleLabel: string;
  /** Crew-rostered roles carry a crew; day roles leave it absent. */
  crewId?: CrewId;
};

export type TrainingRecord = {
  personId: string;
  documentId: string;
  dueAt: string;
  signedVersion?: string;
  signedAt?: string;
};

// [syn] Nine-person M8 roster, in Paul's row order. Crew letters are carried only
// by crew-rostered seats; Day is derived for the four day roles in the transform.
export const TRAINING_ROSTER: readonly TrainingRosterMember[] = [
  { personId: 'person-senior-met', seatId: 'seat-senior-met', roleId: 'senior-metallurgist', roleLabel: 'Senior Metallurgist' },
  { personId: 'person-spark', seatId: 'seat-met', roleId: 'metallurgist', roleLabel: 'Control Metallurgist' },
  { personId: 'person-kbrennan', seatId: 'seat-supt', roleId: 'superintendent', roleLabel: 'Operations Superintendent' },
  { personId: 'person-dlee', seatId: 'seat-ops-supervisor', roleId: 'ops-supervisor', roleLabel: 'Shift Supervisor', crewId: 'crew-c' },
  { personId: 'person-mnavarro', seatId: 'seat-ops-supervisor', roleId: 'ops-supervisor', roleLabel: 'Shift Supervisor', crewId: 'crew-b' },
  { personId: 'person-dokafor', seatId: 'seat-cro', roleId: 'cro', roleLabel: 'Control Room Operator', crewId: 'crew-c' },
  { personId: 'person-dpham', seatId: 'seat-cro', roleId: 'cro', roleLabel: 'Control Room Operator', crewId: 'crew-a' },
  { personId: 'person-tobi', seatId: 'seat-cro', roleId: 'cro', roleLabel: 'Control Room Operator', crewId: 'crew-b' },
  { personId: 'person-bfletcher', seatId: 'seat-maintenance', roleId: 'maintenance', roleLabel: 'Maintenance Supervisor' },
];

// [syn] One person × required document row per role assignment. Missing or stale
// signatures remain explicit rows with their due date; current versions come
// from all nine assigned documents.
export const TRAINING_RECORDS: readonly TrainingRecord[] = [
  // [syn] L. Chen — Senior metallurgist, five requirements; all current.
  { personId: 'person-senior-met', documentId: 'PR-01', dueAt: '2026-12-01', signedVersion: 'r3', signedAt: '2026-06-02' },
  { personId: 'person-senior-met', documentId: 'PR-02', dueAt: '2026-12-01', signedVersion: 'r2', signedAt: '2026-05-10' },
  { personId: 'person-senior-met', documentId: 'RS-01', dueAt: '2026-12-01', signedVersion: 'v4', signedAt: '2026-06-02' },
  { personId: 'person-senior-met', documentId: 'RS-02', dueAt: '2026-12-01', signedVersion: 'v2', signedAt: '2026-03-20' },
  { personId: 'person-senior-met', documentId: 'TM-03', dueAt: '2026-12-01', signedVersion: 'v3', signedAt: '2026-06-02' },
  // [syn] S. Park — Control metallurgist; the open round sheet is not overdue yet.
  { personId: 'person-spark', documentId: 'PR-01', dueAt: '2026-12-01', signedVersion: 'r3', signedAt: '2026-06-02' },
  { personId: 'person-spark', documentId: 'PR-02', dueAt: '2026-12-01', signedVersion: 'r2', signedAt: '2026-05-10' },
  { personId: 'person-spark', documentId: 'RS-01', dueAt: '2026-12-01', signedVersion: 'v4', signedAt: '2026-06-02' },
  { personId: 'person-spark', documentId: 'RS-02', dueAt: '2026-06-30' },
  { personId: 'person-spark', documentId: 'TM-03', dueAt: '2026-12-01', signedVersion: 'v3', signedAt: '2026-06-02' },
  // [syn] K. Brennan — Operations superintendent; TM-02 is newly published.
  { personId: 'person-kbrennan', documentId: 'PR-01', dueAt: '2026-12-01', signedVersion: 'r3', signedAt: '2026-06-02' },
  { personId: 'person-kbrennan', documentId: 'PR-02', dueAt: '2026-12-01', signedVersion: 'r2', signedAt: '2026-05-10' },
  { personId: 'person-kbrennan', documentId: 'PR-04', dueAt: '2026-12-01', signedVersion: 'r2', signedAt: '2026-03-10' },
  { personId: 'person-kbrennan', documentId: 'TM-01', dueAt: '2026-12-01', signedVersion: 'v2', signedAt: '2026-02-10' },
  { personId: 'person-kbrennan', documentId: 'TM-02', dueAt: '2026-06-30' },
  // [syn] D. Lee — Crew C shift supervisor; RS-02 is overdue.
  { personId: 'person-dlee', documentId: 'PR-01', dueAt: '2026-12-01', signedVersion: 'r3', signedAt: '2026-06-02' },
  { personId: 'person-dlee', documentId: 'PR-02', dueAt: '2026-12-01', signedVersion: 'r2', signedAt: '2026-05-10' },
  { personId: 'person-dlee', documentId: 'PR-03', dueAt: '2026-12-01', signedVersion: 'r1', signedAt: '2026-02-10' },
  { personId: 'person-dlee', documentId: 'PR-04', dueAt: '2026-12-01', signedVersion: 'r2', signedAt: '2026-03-10' },
  { personId: 'person-dlee', documentId: 'RS-01', dueAt: '2026-12-01', signedVersion: 'v4', signedAt: '2026-06-02' },
  { personId: 'person-dlee', documentId: 'RS-02', dueAt: '2026-06-07' },
  { personId: 'person-dlee', documentId: 'TM-01', dueAt: '2026-12-01', signedVersion: 'v2', signedAt: '2026-02-10' },
  // [syn] M. Navarro — Crew B; PR-01 r2 predates r3, and PR-03 remains missing.
  { personId: 'person-mnavarro', documentId: 'PR-01', dueAt: '2026-06-07', signedVersion: 'r2', signedAt: '2026-05-10' },
  { personId: 'person-mnavarro', documentId: 'PR-02', dueAt: '2026-12-01', signedVersion: 'r2', signedAt: '2026-05-10' },
  { personId: 'person-mnavarro', documentId: 'PR-03', dueAt: '2026-06-07' },
  { personId: 'person-mnavarro', documentId: 'PR-04', dueAt: '2026-12-01', signedVersion: 'r2', signedAt: '2026-03-10' },
  { personId: 'person-mnavarro', documentId: 'RS-01', dueAt: '2026-12-01', signedVersion: 'v4', signedAt: '2026-06-02' },
  { personId: 'person-mnavarro', documentId: 'RS-02', dueAt: '2026-12-01', signedVersion: 'v2', signedAt: '2026-03-20' },
  { personId: 'person-mnavarro', documentId: 'TM-01', dueAt: '2026-12-01', signedVersion: 'v2', signedAt: '2026-02-10' },
  // [syn] D. Okafor — Crew C CRO; current on all seven assigned documents.
  { personId: 'person-dokafor', documentId: 'PR-01', dueAt: '2026-12-01', signedVersion: 'r3', signedAt: '2026-06-02' },
  { personId: 'person-dokafor', documentId: 'PR-02', dueAt: '2026-12-01', signedVersion: 'r2', signedAt: '2026-05-10' },
  { personId: 'person-dokafor', documentId: 'PR-03', dueAt: '2026-12-01', signedVersion: 'r1', signedAt: '2026-02-10' },
  { personId: 'person-dokafor', documentId: 'PR-04', dueAt: '2026-12-01', signedVersion: 'r2', signedAt: '2026-03-10' },
  { personId: 'person-dokafor', documentId: 'RS-01', dueAt: '2026-12-01', signedVersion: 'v4', signedAt: '2026-06-02' },
  { personId: 'person-dokafor', documentId: 'RS-02', dueAt: '2026-12-01', signedVersion: 'v2', signedAt: '2026-03-20' },
  { personId: 'person-dokafor', documentId: 'TM-01', dueAt: '2026-12-01', signedVersion: 'v2', signedAt: '2026-02-10' },
  // [syn] D. Pham — Crew A CRO; three noncritical records are overdue.
  { personId: 'person-dpham', documentId: 'PR-01', dueAt: '2026-12-01', signedVersion: 'r3', signedAt: '2026-06-02' },
  { personId: 'person-dpham', documentId: 'PR-02', dueAt: '2026-12-01', signedVersion: 'r2', signedAt: '2026-05-10' },
  { personId: 'person-dpham', documentId: 'PR-03', dueAt: '2026-06-07' },
  { personId: 'person-dpham', documentId: 'PR-04', dueAt: '2026-12-01', signedVersion: 'r2', signedAt: '2026-03-10' },
  { personId: 'person-dpham', documentId: 'RS-01', dueAt: '2026-06-07' },
  { personId: 'person-dpham', documentId: 'RS-02', dueAt: '2026-06-07' },
  { personId: 'person-dpham', documentId: 'TM-01', dueAt: '2026-12-01', signedVersion: 'v2', signedAt: '2026-02-10' },
  // [syn] T. Obi — Crew B CRO; PR-01 r2 predates r3, RS-01 is also overdue.
  { personId: 'person-tobi', documentId: 'PR-01', dueAt: '2026-06-07', signedVersion: 'r2', signedAt: '2026-05-11' },
  { personId: 'person-tobi', documentId: 'PR-02', dueAt: '2026-12-01', signedVersion: 'r2', signedAt: '2026-05-10' },
  { personId: 'person-tobi', documentId: 'PR-03', dueAt: '2026-12-01', signedVersion: 'r1', signedAt: '2026-02-10' },
  { personId: 'person-tobi', documentId: 'PR-04', dueAt: '2026-12-01', signedVersion: 'r2', signedAt: '2026-03-10' },
  { personId: 'person-tobi', documentId: 'RS-01', dueAt: '2026-06-07' },
  { personId: 'person-tobi', documentId: 'RS-02', dueAt: '2026-12-01', signedVersion: 'v2', signedAt: '2026-03-20' },
  { personId: 'person-tobi', documentId: 'TM-01', dueAt: '2026-12-01', signedVersion: 'v2', signedAt: '2026-02-10' },
  // [syn] B. Fletcher — Maintenance supervisor; TM-02 is newly published.
  { personId: 'person-bfletcher', documentId: 'PR-01', dueAt: '2026-12-01', signedVersion: 'r3', signedAt: '2026-06-02' },
  { personId: 'person-bfletcher', documentId: 'PR-02', dueAt: '2026-12-01', signedVersion: 'r2', signedAt: '2026-05-10' },
  { personId: 'person-bfletcher', documentId: 'PR-04', dueAt: '2026-12-01', signedVersion: 'r2', signedAt: '2026-03-10' },
  { personId: 'person-bfletcher', documentId: 'TM-02', dueAt: '2026-06-30' },
];

// ---------------------------------------------------------------------------
// Union + lookup
// ---------------------------------------------------------------------------

export type DocumentRecord = ProcedureDoc | RoundSheetDoc | TrainingDoc;

export function allDocuments(): readonly DocumentRecord[] {
  return [...PROCEDURES, ...ROUND_SHEETS, ...TRAINING_MODULES];
}

export function documentById(id: string | null | undefined): DocumentRecord | undefined {
  if (!id) return undefined;
  return allDocuments().find((doc) => doc.id === id);
}

/** Default document per persona (the lens rule) — v19 M7/M8 audience split. */
export const DEFAULT_DOCUMENT_BY_PERSONA: Readonly<Partial<Record<Persona, string>>> = {
  Superintendent: 'PR-01',
  Maintenance: 'PR-01',
  GM: 'PR-01',
  Metallurgist: 'PR-01',
  'Control room': 'RS-01',
  'Ops supervisor': 'RS-01',
  Operator: 'RS-01',
  'Senior metallurgist': 'TM-03',
};
